import * as THREE from 'three';
import { createMatteMaterial, fbm3d, palette } from '../../visual';
import type { Terrain } from './Terrain';
import type { SurfaceObstacle } from './SurfaceObstacle';

export interface SurfaceRocksOptions {
  readonly count: number;
  readonly tint: number;
  readonly accentTint: number;
  readonly minSize: number;
  readonly maxSize: number;
  readonly spread: number;
  readonly excludeRadius?: number;
}

export interface SurfaceRocksResult {
  readonly group: THREE.Group;
  readonly obstacles: readonly SurfaceObstacle[];
}

export function scatterSurfaceRocks(
  terrain: Terrain,
  options: SurfaceRocksOptions
): SurfaceRocksResult {
  const group = new THREE.Group();
  group.name = 'surface-rocks';
  const obstacles: SurfaceObstacle[] = [];
  const excludeSq = (options.excludeRadius ?? 0) ** 2;

  const baseMat = createMatteMaterial({
    color: options.tint,
    roughness: 0.95,
    flatShading: true
  });
  const accentMat = createMatteMaterial({
    color: options.accentTint,
    roughness: 0.92,
    flatShading: true
  });

  for (let i = 0; i < options.count; i++) {
    let tries = 0;
    let x = 0;
    let z = 0;
    while (tries++ < 6) {
      x = (Math.random() - 0.5) * options.spread;
      z = (Math.random() - 0.5) * options.spread;
      if (x * x + z * z >= excludeSq) break;
    }

    const size =
      options.minSize + Math.random() * (options.maxSize - options.minSize);
    const mesh = buildRockMesh(size, Math.random() < 0.22 ? accentMat : baseMat);

    const y = terrain.getHeightAt(x, z) + size * 0.25;
    mesh.position.set(x, y, z);
    mesh.rotation.set(
      Math.random() * 0.4,
      Math.random() * Math.PI * 2,
      Math.random() * 0.4
    );
    group.add(mesh);

    obstacles.push({
      position: mesh.position.clone(),
      radius: size * 0.9,
      // Rock height above its base — matches the visible silhouette so
      // the surface ship can fly cleanly over scatter rocks.
      height: size * 1.1
    });
  }

  return { group, obstacles };
}

function buildRockMesh(size: number, material: THREE.Material): THREE.Mesh {
  const geometry = new THREE.IcosahedronGeometry(size, 1);
  const position = geometry.attributes['position'] as THREE.BufferAttribute;

  const seedX = Math.random() * 30;
  const seedY = Math.random() * 30;
  const seedZ = Math.random() * 30;

  const axis = new THREE.Vector3(
    0.85 + Math.random() * 0.3,
    0.6 + Math.random() * 0.3,
    0.85 + Math.random() * 0.3
  );

  const dir = new THREE.Vector3();
  for (let i = 0; i < position.count; i++) {
    dir.set(position.getX(i), position.getY(i), position.getZ(i)).normalize();
    const n = fbm3d(
      dir.x * 1.6 + seedX,
      dir.y * 1.6 + seedY,
      dir.z * 1.6 + seedZ,
      3
    );
    const d = 1 + n * 0.22;
    const r = size * d;
    position.setXYZ(
      i,
      dir.x * axis.x * r,
      dir.y * axis.y * r,
      dir.z * axis.z * r
    );
  }
  position.needsUpdate = true;
  geometry.computeVertexNormals();
  return new THREE.Mesh(geometry, material);
}

export function tintForPlanet(accentHex: number, baseHex: number): {
  tint: number;
  accentTint: number;
} {
  return {
    tint: mixHex(baseHex, palette.asteroid.body, 0.5),
    accentTint: mixHex(accentHex, palette.asteroid.accent, 0.5)
  };
}

function mixHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}
